// Type-only imports, so that this module stays free of runtime dependencies and can be used from the pure store.
import type { ReactElement } from 'react'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import type { NormalizedError } from '../../reducers/errorReducer'

export enum QueueStatus {
    Dispatch = 'DISPATCH',
    Duplicate = 'DUPLICATE',
    Queued = 'QUEUED',
    Delayed = 'DELAYED',
    Cancelled = 'CANCELLED',
    Finished = 'FINISHED',
    Failed = 'FAILED',
}

/** Statuses after which no further transition is expected, so the job can leave the queue. */
export const terminalQueueStatuses = [QueueStatus.Finished, QueueStatus.Failed]

/** Statuses in which the job can no longer be cancelled. */
export const settledQueueStatuses = [QueueStatus.Cancelled, QueueStatus.Duplicate, QueueStatus.Failed, QueueStatus.Finished]

/**
 * Which flavour of job this is. Determines the toast that renders it and how it is cancelled, and lets a single
 * toaster render every kind of job without the render function having to be threaded through the dispatcher.
 */
export enum QueueJobKind {
    Document = 'DOCUMENT',
    EmailFiling = 'EMAIL_FILING',
    TimeSuggestion = 'TIME_SUGGESTION',
}

/**
 * The queue job id for a time suggestion, which doubles as the toast id.
 *
 * Two routes offer the same suggestion. The websocket notification shows one as it arrives and uses the bare
 * suggestion id; the counter shows a batch on request and appends the round, because Fluent silently ignores a
 * toast whose id it has seen before, so asking a second time has to arrive under a new id. Both are right, and
 * both have to be recognised by whoever wants to close such a toast again - hence one place that builds the id
 * and one that matches it.
 */
export function timeSuggestionJobId(timeSuggestionId: string, round?: number): string {
    return round === undefined ? timeSuggestionId : `${timeSuggestionId}-${round}`
}

/** Whether a queue job or toast id belongs to this time suggestion, whichever route raised it. */
export function isTimeSuggestionJob(jobId: string, timeSuggestionId: string): boolean {
    return jobId === timeSuggestionId || jobId.startsWith(`${timeSuggestionId}-`)
}

/** Service Bus correlation ids are capped at 128 characters. */
export const MAX_REQUEST_ID_LENGTH = 128

export function truncateRequestId(requestId: string): string {
    return requestId.substring(0, Math.min(requestId.length, MAX_REQUEST_ID_LENGTH))
}

// The data that applies to the whole chain of events.
export type QueueContextData<RequestT, StatusT, ContextT> = ContextT &
    Pick<AxiosRequestConfig<RequestT>, 'baseURL' | 'url' | 'method' | 'data'> & {
        // Mapper to extract the Azure SB message ID from the dispatch request.
        mapDispatchResponse: (response: AxiosResponse<unknown>) => any
        // Any status callbacks that are registered by the dispatcher.
        callbacks?: QueueCallbacks<RequestT, StatusT, ContextT>
        // The controller to emit an abortion signal to outgoing requests to the backend that have not yet been enqueued.
        abortController?: AbortController
        // A custom title to show in toaster messages that represents the document that's being generated.
        title?: ReactElement | string
    }

// The data that is specific for a given status transition and will not persist through the whole chain of events.
// The StatusT is optional, because not every status will include it.
export type QueueStatusData<StatusT = {}> = StatusT & {
    current?: QueueStatus
    error?: Pick<NormalizedError, 'message' | 'trace'>
}

export type QueueData<RequestT, StatusT, ContextT> = {
    kind: QueueJobKind
    context: QueueContextData<RequestT, StatusT, ContextT>
    status: QueueStatusData<StatusT>
}

// Callbacks are called after updates.
export type QueueCallbacks<RequestT, StatusT, ContextT> = {
    [key in QueueStatus]?: (data: QueueData<RequestT, StatusT, ContextT> | undefined) => void
}

/** A job with its generics erased, for the toaster which renders every kind. */
export type AnyQueueData = QueueData<unknown, any, any>
